import React, { useState, useEffect } from 'react';
import { Save, Globe, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { getGeneralSettings, updateGeneralSettings } from '@/services/settingsApi';

export const GeneralSettingsTab = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [siteName, setSiteName] = useState('');
  const [siteTagline, setSiteTagline] = useState('');
  const [siteDescription, setSiteDescription] = useState('');
  const [contactEmail, setContactEmail] = useState('');

  useEffect(() => {
    const fetch = async () => {
      try {
        setLoading(true);
        const res = await getGeneralSettings();
        const data = res?.data || res || {};
        setSiteName(data.site_name || '');
        setSiteTagline(data.site_tagline || '');
        setSiteDescription(data.site_description || '');
        setContactEmail(data.contact_email || '');
      } catch (error) {
        console.error(error);
        toast.error('Failed to load general settings');
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateGeneralSettings({
        site_name: siteName,
        site_tagline: siteTagline,
        site_description: siteDescription,
        contact_email: contactEmail,
      });
      toast.success('General settings saved');
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card className="rounded-xl border-slate-200 shadow-sm">
        <CardContent className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="rounded-xl border-slate-200 shadow-sm">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Globe className="h-5 w-5" />
          General Settings
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <Label>Site Name</Label>
          <Input value={siteName} onChange={(e) => setSiteName(e.target.value)} className="mt-2" />
        </div>
        <div>
          <Label>Site Tagline</Label>
          <Input value={siteTagline} onChange={(e) => setSiteTagline(e.target.value)} className="mt-2" />
        </div>
        <div>
          <Label>Site Description</Label>
          <Textarea
            value={siteDescription}
            onChange={(e) => setSiteDescription(e.target.value)}
            className="mt-2"
            rows={3}
          />
        </div>
        <div>
          <Label>Contact Email</Label>
          <Input
            value={contactEmail}
            onChange={(e) => setContactEmail(e.target.value)}
            type="email"
            className="mt-2"
          />
        </div>
        <Button onClick={handleSave} disabled={saving}>
          {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
          Save Changes
        </Button>
      </CardContent>
    </Card>
  );
};